import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useFormDrawerContext } from '@/app/surfaces/form-drawer-provider.js';
import { useQuickPanelContext } from '@/app/surfaces/quick-panel-provider.js';
import { useSurfaceNavigation } from '@/app/surfaces/use-surface-navigation.js';
import { ExpandableNavSidebar } from '@/shared/shell/expandable-nav-sidebar.js';
import {
  resolveInitialOpenGroups,
  resolveOpenGroupsAfterPathnameChange,
} from '@/shared/shell/nav-open-groups.js';
import {
  SHELL_SIDEBAR_EXPANDED_STORAGE_KEY,
  SHELL_SIDEBAR_WIDTH_COLLAPSED,
  SHELL_SIDEBAR_WIDTH_EXPANDED,
  type ShellNavEntry,
  type ShellNavTaxonomyEntity,
} from '@/shared/shell/nav.types.js';

function readExpandedPreference(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(SHELL_SIDEBAR_EXPANDED_STORAGE_KEY) === 'true';
}

function isShellNavTaxonomyEntity(value: unknown): value is ShellNavTaxonomyEntity {
  return (
    value === 'categories' ||
    value === 'brands' ||
    value === 'product-types' ||
    value === 'service-types' ||
    value === 'manufacturers' ||
    value === 'measurement-units'
  );
}

export interface AppShellNavSidebarProps {
  entries: readonly ShellNavEntry[];
  footerExtras?: ReactNode;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export function AppShellNavSidebar({
  entries,
  footerExtras,
  expanded: expandedProp,
  onExpandedChange: onExpandedChangeProp,
}: AppShellNavSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { openScreen, closeAllQuickPanels } = useSurfaceNavigation();
  const { stack } = useQuickPanelContext();
  const { openFormDrawer } = useFormDrawerContext();
  const [expandedInternal, setExpandedInternal] = useState(readExpandedPreference);
  const expanded = expandedProp ?? expandedInternal;
  const setExpanded = onExpandedChangeProp ?? setExpandedInternal;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    resolveInitialOpenGroups(location.pathname, entries),
  );
  const previousPathnameRef = useRef(location.pathname);

  useEffect(() => {
    if (expandedProp !== undefined) {
      return;
    }

    window.localStorage.setItem(SHELL_SIDEBAR_EXPANDED_STORAGE_KEY, expanded ? 'true' : 'false');
  }, [expanded, expandedProp]);

  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    setOpenGroups((current) =>
      resolveOpenGroupsAfterPathnameChange({
        previousPathname,
        nextPathname: location.pathname,
        entries,
        currentOpenGroups: current,
      }),
    );
  }, [entries, location.pathname]);

  const activeQuickPanelEntity = useMemo(() => {
    const top = stack.at(-1);
    if (top?.key !== 'catalog-taxonomy-list') {
      return null;
    }

    const entity = top.params['entity'];
    return isShellNavTaxonomyEntity(entity) ? entity : null;
  }, [stack]);

  useEffect(() => {
    if (!activeQuickPanelEntity) {
      return;
    }

    setOpenGroups((current) => ({
      ...current,
      catalog: true,
    }));
  }, [activeQuickPanelEntity]);

  return (
    <ExpandableNavSidebar
      entries={entries}
      pathname={location.pathname}
      expanded={expanded}
      openGroups={openGroups}
      activeQuickPanelEntity={activeQuickPanelEntity}
      footerExtras={footerExtras}
      onExpandedChange={setExpanded}
      onNavigate={(path) => {
        const homeEntry = entries.find(
          (entry): entry is Extract<ShellNavEntry, { kind: 'link' }> =>
            entry.kind === 'link' && entry.id === 'home' && entry.path !== undefined,
        );

        if (path === homeEntry?.path) {
          closeAllQuickPanels();
        }

        void navigate(path);
      }}
      onOpenGroup={(groupId) => {
        setOpenGroups((current) => ({
          ...current,
          [groupId]: true,
        }));
      }}
      onCloseGroup={(groupId) => {
        setOpenGroups((current) => ({
          ...current,
          [groupId]: false,
        }));
      }}
      onOpenQuickPanel={(_panelKey, params) => {
        const entity = params['entity'];
        const title = params['title'];
        if (!isShellNavTaxonomyEntity(entity) || typeof title !== 'string') {
          return;
        }

        closeAllQuickPanels();
        openScreen('catalog.taxonomies.list', {
          mode: 'quickPanel',
          params: { entity, title },
        });
      }}
      onOpenFormDrawer={(drawerKey, params) => {
        closeAllQuickPanels();
        openFormDrawer(drawerKey, params);
      }}
    />
  );
}

export function resolveAppShellSidebarWidth(expanded: boolean): string {
  return expanded ? SHELL_SIDEBAR_WIDTH_EXPANDED : SHELL_SIDEBAR_WIDTH_COLLAPSED;
}

export function readAppShellSidebarExpanded(): boolean {
  return readExpandedPreference();
}
